/**
 * Tag-Input für die Liste der Funktionen eines Mitarbeiters
 * (zusätzlich zur Hauptfunktion `position`).
 *
 * Bedienlogik:
 *  - Eingabe + Enter (oder Komma) fügt ein Tag hinzu
 *  - Klick auf das ✕ am Tag entfernt es
 *  - Hauptfunktion (`primaryPosition`) wird zwar im Backend automatisch in
 *    die Liste übernommen, aber wir blenden sie hier optisch ein, damit der
 *    User sieht „diese Funktion ist immer schon enthalten" — sie kann hier
 *    nicht entfernt werden.
 */
import React from 'react';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { X } from 'lucide-react';

export const PositionListEditor = ({ positionList = [], primaryPosition, onChange }) => {
  const [draft, setDraft] = React.useState('');
  // Liste ohne Hauptfunktion (wird separat als unveränderliches Badge angezeigt)
  const extras = positionList.filter((p) => p && p !== primaryPosition);

  const addTag = () => {
    const val = draft.trim();
    if (!val) return;
    if (val === primaryPosition) {
      setDraft('');
      return;
    }
    if (extras.includes(val)) {
      setDraft('');
      return;
    }
    const next = primaryPosition ? [primaryPosition, ...extras, val] : [...extras, val];
    onChange(next);
    setDraft('');
  };

  const removeTag = (tag) => {
    const next = extras.filter((p) => p !== tag);
    onChange(primaryPosition ? [primaryPosition, ...next] : next);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag();
    } else if (e.key === 'Backspace' && !draft && extras.length > 0) {
      removeTag(extras[extras.length - 1]);
    }
  };

  return (
    <div className="space-y-2" data-testid="position-list-editor">
      <Input
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={addTag}
        placeholder="Funktion eingeben + Enter (z. B. SP-Fachkraft)"
        data-testid="position-list-input"
      />
      {(primaryPosition || extras.length > 0) && (
        <div className="flex flex-wrap gap-1.5">
          {primaryPosition && (
            <Badge variant="secondary" className="gap-1" title="Hauptfunktion — kannst du oben ändern">
              {primaryPosition}
              <span className="text-[10px] text-muted-foreground">★ Haupt</span>
            </Badge>
          )}
          {extras.map((tag) => (
            <Badge
              key={tag}
              variant="outline"
              className="gap-1 cursor-pointer hover:bg-destructive/10 hover:border-destructive/40"
              onClick={() => removeTag(tag)}
              data-testid={`position-tag-${tag}`}
              title="Klicken zum Entfernen"
            >
              {tag}
              <X className="h-3 w-3" />
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
};

export default PositionListEditor;
